'use client';
import { useRouter, useSearchParams } from 'next/navigation';
import { ROUTES } from '@constants/routes.constants';
import { useGetUserQuery } from '@lib/redux/api/user-api';
import { useEffect } from 'react';
import { useProcessOAuthMutation } from '@lib/redux/api/e-commerce-api';
import { useAnalytics } from '@ui/analytics';

export default function RootPage() {
  const searchParams = useSearchParams();
  const { track } = useAnalytics();

  const shopifyAuthCode = searchParams.get('code');
  const shopifyShopDomain = searchParams.get('shop');

  const [processOAuth] = useProcessOAuthMutation();

  const { data, isSuccess, isLoading } = useGetUserQuery();

  const router = useRouter();

  useEffect(() => {
    router.prefetch(ROUTES.dashboard.root);
    router.prefetch(ROUTES.auth);
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isSuccess && data) {
      router.push(ROUTES.dashboard.root);
      return;
    }

    if (shopifyAuthCode && shopifyShopDomain) {
      // Analytics tracking for Shopify OAuth processing
      track({
        eventName: 'shopify_oauth_processing_started',
        properties: {
          shop_domain: shopifyShopDomain,
          has_auth_code: !!shopifyAuthCode,
          timestamp: new Date().toISOString(),
        },
      });

      // trigger shopify oauth processing
      processOAuth({ authorization_code: shopifyAuthCode, shop_domain: shopifyShopDomain })
        .unwrap()
        .then((res) => {
          // Analytics tracking for successful Shopify OAuth
          track({
            eventName: 'shopify_oauth_processing_success',
            properties: {
              shop_domain: shopifyShopDomain,
              shop_id: res.id,
              shop_email: res.email,
              shop_owner_name: res.shop_owner_name,
              timestamp: new Date().toISOString(),
            },
          });

          router.push(
            `${ROUTES.auth}?shopify_shop_id=${res.id}&shopify_email=${res.email}&shopify_name=${res.shop_owner_name}`,
          );
        })
        .catch((error) => {
          // Analytics tracking for failed Shopify OAuth
          track({
            eventName: 'shopify_oauth_processing_failed',
            properties: {
              shop_domain: shopifyShopDomain,
              error_message: error.message || 'Unknown error',
              timestamp: new Date().toISOString(),
            },
          });

          // TODO: what should we do again
          console.error('Error during OAuth processing:', error);

          router.push(ROUTES.auth);
        });
    } else if (shopifyShopDomain) {
      // Analytics tracking for Shopify OAuth redirect
      track({
        eventName: 'shopify_oauth_redirect_initiated',
        properties: {
          shop_domain: shopifyShopDomain,
          timestamp: new Date().toISOString(),
        },
      });

      const shopifyOAuthUrl = process.env.NEXT_PUBLIC_SHOPIFY_OAUTH_URL;

      window.location.href = shopifyOAuthUrl;
    } else {
      router.push(ROUTES.auth);
    }
  }, [isSuccess, isLoading, shopifyAuthCode, shopifyShopDomain]);

  return null;
}
